import type { TourStep } from './tour-step';

export type ShellTourAudience = 'employee' | 'customer';

export type ShellTourOptions = {
  audience: ShellTourAudience;
  /** `roi_settings.enabled` on the project config. */
  isRoiEnabled: boolean;
  /** `aifi_settings.enabled` on the project config. */
  isProficiencyEnabled: boolean;
};

export const APP_SHELL_FOOTER_TUTORIAL_SLOT_ID = 'app-shell-footer-tutorial-slot';

/** Reports lands right after the analytics subproducts, before the supporting steps. */
const SHELL_TOUR_EMPLOYEE_STEP_KEYS = [
  'organization',
  'experience',
  'fluency',
  'roi',
  'reports',
  'live-data',
  'taxonomy',
  'roi-setup',
  'filters',
  'settings',
] as const;

/**
 * Customer-agent projects have no ROI, Proficiency, or ROI Setup rail items, and reach
 * Reports straight after Experience.
 */
const SHELL_TOUR_CUSTOMER_STEP_KEYS = [
  'organization',
  'experience',
  'reports',
  'live-data',
  'taxonomy',
  'filters',
  'settings',
] as const;

type ShellTourStepKey = (typeof SHELL_TOUR_EMPLOYEE_STEP_KEYS)[number];

/** Steps whose rail item only exists when ROI is enabled on the project. */
const ROI_STEP_KEYS: readonly ShellTourStepKey[] = ['roi', 'roi-setup'];

/** Steps whose rail item only exists when AI Proficiency is enabled on the project. */
const PROFICIENCY_STEP_KEYS: readonly ShellTourStepKey[] = ['fluency'];

type ShellTourStepDefinition = {
  key: ShellTourStepKey;
  name: string;
  headline: string;
  body: string;
  callout: string;
  iconSvg: string;
  element: string;
  side: NonNullable<TourStep['side']>;
  align: NonNullable<TourStep['align']>;
  disableActiveInteraction?: boolean;
};

function lucideIcon(path: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${path}</svg>`;
}

const SHELL_TOUR_STEPS: ShellTourStepDefinition[] = [
  {
    key: 'organization',
    name: 'Organization & project',
    headline: 'Start with your workspace',
    body: 'Everything you see in Nebuly belongs to a project. Use this switcher to move between organizations, projects, or sub-projects. Each project has its own data, reports, taxonomies, and configuration.',
    callout: 'Move between organizations and projects to access the data you need.',
    iconSvg: lucideIcon(
      '<path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/>',
    ),
    element: "[data-tour='sidebar-project-picker']",
    side: 'right',
    align: 'start',
    disableActiveInteraction: false,
  },
  {
    key: 'experience',
    name: 'Experience',
    headline: 'Understand how people experience AI',
    body: 'See what users are trying to accomplish, how they feel, where they encounter friction, and why conversations succeed or fail.',
    callout: 'Answers: what is happening when people use our AI, and why?',
    iconSvg: lucideIcon(
      '<circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" x2="9.01" y1="9" y2="9"/><line x1="15" x2="15.01" y1="9" y2="9"/>',
    ),
    element: "[data-tour='sidebar-experience-nav']",
    side: 'right',
    align: 'start',
  },
  {
    key: 'roi',
    name: 'ROI',
    headline: 'Measure the business value AI creates',
    body: 'ROI connects successful AI usage to estimated time saved and monetary value. See which teams, projects, and use cases create the most value, and where AI investment has the greatest impact.',
    callout: 'Answers: is our AI actually creating measurable value?',
    iconSvg: lucideIcon(
      '<line x1="12" x2="12" y1="2" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>',
    ),
    element: "[data-tour='sidebar-roi-nav']",
    side: 'right',
    align: 'start',
  },
  {
    key: 'fluency',
    name: 'Proficiency',
    headline: 'Measure how effectively people use AI',
    body: 'Proficiency shows how well employees interact with AI, not just how often. Understand prompting quality, compare proficiency across teams, and identify where training could improve results.',
    callout: 'Answers: are our employees getting better at using AI?',
    iconSvg: lucideIcon(
      '<path d="M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z"/><path d="M22 10v6"/><path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5"/>',
    ),
    element: "[data-tour='sidebar-fluency-nav']",
    side: 'right',
    align: 'start',
  },
  {
    key: 'live-data',
    name: 'Live Data',
    headline: 'Go from a metric to the evidence behind it',
    body: 'Live Data contains the interactions, conversations, users, and groups behind your analytics. Use it to investigate a surprising number, understand a failure, or inspect the conversations driving a trend.',
    callout: 'Think of Reports as the summary and Live Data as the evidence.',
    iconSvg: lucideIcon(
      '<ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5V19A9 3 0 0 0 21 19V5"/><path d="M3 12A9 3 0 0 0 21 12"/>',
    ),
    element: "[data-tour='sidebar-live-data-nav']",
    side: 'right',
    align: 'start',
  },
  {
    key: 'taxonomy',
    name: 'Taxonomy',
    headline: 'Turn conversations into structured business data',
    body: 'Taxonomy is how Nebuly understands what your conversations mean. It classifies interactions into dimensions such as topics, intent, failures, sentiment, business risks, and your own custom categories.',
    callout: 'These classifications power your analytics, so changes can affect what you see across reports.',
    iconSvg: lucideIcon(
      '<path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/><path d="M14 6h.01"/><path d="M6 6h.01"/><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/>',
    ),
    element: "[data-tour='sidebar-taxonomy-nav']",
    side: 'right',
    align: 'start',
  },
  {
    key: 'reports',
    name: 'Reports',
    headline: 'This is where everything comes together',
    body: 'Reports turn your data, metrics, and taxonomy into dashboards for monitoring AI usage, investigating behavior, and measuring business impact. The sidebar holds Workspace reports shared with the project, Private reports visible only to you, and Favorites you have starred.',
    callout: 'Open Reports Library to browse everything available, or create your own.',
    iconSvg: lucideIcon(
      '<rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="M12 11h4"/><path d="M12 16h4"/><path d="M8 11h.01"/><path d="M8 16h.01"/>',
    ),
    element: "[data-tour='sidebar']",
    side: 'right',
    align: 'start',
  },
  {
    key: 'roi-setup',
    name: 'ROI Setup',
    headline: 'Measure the business value AI brings to your organization',
    body: 'Configure the assumptions Nebuly uses to calculate ROI, including employee hourly cost, time saved, and which conversations count as successful.',
    callout: 'Use this to ensure ROI reflects the real business impact of your AI.',
    iconSvg: lucideIcon(
      '<rect width="16" height="20" x="4" y="2" rx="2"/><line x1="8" x2="16" y1="6" y2="6"/><line x1="16" x2="16" y1="14" y2="18"/><path d="M16 10h.01"/><path d="M12 10h.01"/><path d="M8 10h.01"/><path d="M12 14h.01"/><path d="M8 14h.01"/><path d="M12 18h.01"/><path d="M8 18h.01"/>',
    ),
    element: "[data-tour='sidebar-roi-setup-nav']",
    side: 'right',
    align: 'start',
  },
  {
    key: 'filters',
    name: 'Filters',
    headline: 'Focus every report on the data that matters',
    body: 'Global filters let you refine all customized reports with a single selection. Build advanced filters using teams, users, topics, tags, sentiment, and other attributes to surface exactly the data you need.',
    callout: 'Apply one filter across all your customized reports.',
    iconSvg: lucideIcon('<polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>'),
    element: "[data-tour='sidebar-filters-nav']",
    side: 'right',
    align: 'start',
  },
  {
    key: 'settings',
    name: 'Settings',
    headline: 'Manage and configure your organization on Nebuly',
    body: 'This is where you can access Nebuly’s official guides, manage project and organization settings, and update your account settings.',
    callout: 'Come here whenever you need to configure Nebuly or find help.',
    iconSvg: lucideIcon(
      '<path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/>',
    ),
    element: "[data-tour='sidebar-settings-section']",
    side: 'right',
    align: 'end',
  },
];

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export function buildShellTourPopoverTitle(
  step: Pick<ShellTourStepDefinition, 'name' | 'iconSvg'>,
  stepIndex: number,
  totalSteps: number,
): string {
  return `<div class="nebuly-shell-tour-header"><span class="nebuly-shell-tour-step-name">${step.iconSvg}<span>${escapeHtml(step.name)}</span></span><span class="nebuly-shell-tour-count">${stepIndex + 1} / ${totalSteps}</span></div>`;
}

export function buildShellTourPopoverDescription(
  step: Pick<ShellTourStepDefinition, 'headline' | 'body' | 'callout'>,
): string {
  return `<p class="nebuly-shell-tour-headline">${escapeHtml(step.headline)}</p><p class="nebuly-shell-tour-body">${escapeHtml(step.body)}</p><p class="nebuly-shell-tour-callout">${escapeHtml(step.callout)}</p>`;
}

export function getShellTourSteps({ audience, isRoiEnabled, isProficiencyEnabled }: ShellTourOptions): TourStep[] {
  const audienceKeys: readonly ShellTourStepKey[] =
    audience === 'customer' ? SHELL_TOUR_CUSTOMER_STEP_KEYS : SHELL_TOUR_EMPLOYEE_STEP_KEYS;
  const keys = audienceKeys.filter((key) => {
    if (ROI_STEP_KEYS.includes(key)) return isRoiEnabled;
    if (PROFICIENCY_STEP_KEYS.includes(key)) return isProficiencyEnabled;
    return true;
  });
  const definitions = keys.map((key) => {
    const step = SHELL_TOUR_STEPS.find((candidate) => candidate.key === key);
    if (!step) throw new Error(`missing shell tour step: ${key}`);
    return step;
  });

  return definitions.map((step, index) => ({
    key: step.key,
    element: step.element,
    route: '/reports',
    title: buildShellTourPopoverTitle(step, index, definitions.length),
    description: buildShellTourPopoverDescription(step),
    side: step.side,
    align: step.align,
    disableActiveInteraction: step.disableActiveInteraction,
  }));
}

export function renderShellTourPopoverChrome(
  footer: HTMLElement,
  footerButtons: HTMLElement,
  closeButton: HTMLButtonElement,
  previousButton: HTMLButtonElement,
  totalSteps: number,
  currentIndex: number,
): void {
  closeButton.textContent = 'Skip';
  closeButton.setAttribute('aria-label', 'Skip tour');
  previousButton.textContent = 'Back';

  if (closeButton.parentElement !== footer) {
    footer.insertBefore(closeButton, footer.firstChild);
  }

  let dots = footer.querySelector<HTMLElement>('.nebuly-shell-tour-dots');
  if (!dots) {
    dots = document.createElement('div');
    dots.className = 'nebuly-shell-tour-dots';
    dots.setAttribute('aria-hidden', 'true');
    footer.insertBefore(dots, footerButtons);
  }

  dots.replaceChildren();
  for (let index = 0; index < totalSteps; index += 1) {
    const dot = document.createElement('span');
    dot.className = index === currentIndex ? 'nebuly-shell-tour-dot is-active' : 'nebuly-shell-tour-dot';
    dots.appendChild(dot);
  }
}
